import i18n from "@plugins/i18n";
import DOMUtils from "@utils/dom.utils";
import SpeechEvaluationModal from "@plugins/richtext/plugins/speech-evaluation/SpeechEvaluationModal";
import { createApp } from "vue";
import SpeechEvaluation from "@plugins/richtext/plugins/speech-evaluation/SpeechEvaluation";
import richTextUtils from "@plugins/richtext/utils/richtext.utils";
import Modal from "@plugins/modal";
import AppInstanceUtils from "@plugins/common/service/app.instance.utils";
import { SpeechEvaluationEventBus } from "@plugins/richtext/plugins/speech-evaluation/service/event-bus";

export default function (editor) {
  const richTextEditor = editor.options.get("richTextEditor");
  const context = richTextEditor.richTextContext;
  let modalId = null;

  (function init() {
    richTextEditor.plugins["speech_evaluation"] = {
      renderInEditor,
      cleanUp,
      containPluginElements,
      openSpeechEvaluationModal,
    };
  })();

  function renderInEditor() {
    renderInlineSpeechEvaluation(".sls-speech-evaluation");
  }

  function renderInlineSpeechEvaluation(selector) {
    DOMUtils.elOf(editor.getBody(), selector).forEach((element) => {
      const propsData = {
        speechEvaluationText: element.innerHTML,
        showActionTools: true,
      };
      //wrap in non-editable wrapper
      richTextUtils.encloseElementWithWrapper(element, {
        class: "sls-speech-evaluation-wrapper",
        contenteditable: false,
      });

      const SpeechEvaluationApp = createApp(SpeechEvaluation, {
        ...AppInstanceUtils.generateDynamicMountProps(richTextEditor),
        ...propsData,
      });
      AppInstanceUtils.installCommonPlugin(SpeechEvaluationApp);
      const richTextSpeechEvaluation = SpeechEvaluationApp.mount(element);
      DOMUtils.replaceComponentWrapperWithComponentElement(richTextSpeechEvaluation);
      let seWrapper = richTextSpeechEvaluation.$el.closest(".sls-speech-evaluation-wrapper");
      richTextUtils.addNewLineIfIsLastElement(seWrapper);
    });
  }

  function cleanUp(content) {
    let container = document.createElement("div");
    let containerAfterCleanUp = document.createElement("div");
    container.innerHTML = content;
    containerAfterCleanUp.innerHTML = content;

    // remove placeholder
    DOMUtils.elOf(containerAfterCleanUp, ".attachment-placeholder").forEach((element) => {
      element.remove();
    });
    DOMUtils.elOf(containerAfterCleanUp, ".sls-speech-evaluation").forEach((element) => {
      cleanUpElement(containerAfterCleanUp, element);
    });

    return containerAfterCleanUp.innerHTML;
  }

  function cleanUpElement(container, element) {
    const seWrapper = element.closest(".sls-speech-evaluation-wrapper");
    if (seWrapper) {
      seWrapper.replaceWith(element);
    }
  }

  function containPluginElements(container) {
    return container.querySelectorAll(".sls-speech-evaluation").length > 0;
  }

  function handlePluginRerender(target) {
    let seComponent = target.closest(".speech-evaluation-component");
    if (!seComponent || seComponent.length === 0 || seComponent.__vue__) return;

    let targetNode = DOMUtils.elOf(seComponent, ".sls-speech-evaluation")[0];
    if (!targetNode) return;

    cleanUpElement(editor.getBody(), targetNode);
    renderInEditor();
  }

  function handleDisplaySpeechEvaluation(data) {
    richTextEditor.$modal.remove(modalId);
    createInLineSpeechEvaluationAttachment({ speechEvaluationText: data });
  }

  function createInLineSpeechEvaluationAttachment(data) {
    let placeholder = DOMUtils.createElement("div", { class: "sls-speech-evaluation" });
    let splitText = richTextUtils.splitTextByNewLine(data.speechEvaluationText);
    splitText.forEach((text) => {
      // pre will preserves spaces & line breaks
      // this is used in case there are multiple white spaces in the middle
      let childParagraph = DOMUtils.createElement("pre", { class: "paragraph" });
      // convert only leading whitespace to &nbsp;
      // otherwise the text might break unexpectedly
      let indexOfFirstNonSpace = text.search(/([^\s]+)/);
      let processedText =
        text.substring(0, indexOfFirstNonSpace).replace(/ /g, "\u00a0") +
        text.substring(indexOfFirstNonSpace);
      childParagraph.innerHTML = processedText;
      placeholder.appendChild(childParagraph);
    });

    editor.undoManager.ignore(() => {
      editor.insertContent(placeholder.outerHTML);
    });
    renderInlineSpeechEvaluation(".sls-speech-evaluation");
    editor.save();
  }

  function openSpeechEvaluationModal() {
    if (validationToCreate()) {
      openModal();
    }
  }

  function openModal() {
    let speechEvaluationConfig = {};
    let eventBusWatchInstances = [];
    eventBusWatchInstances.push(
      SpeechEvaluationEventBus.$on("embed-speechEvaluation", handleDisplaySpeechEvaluation),
    );
    speechEvaluationConfig.eventBusWatchInstances = eventBusWatchInstances;
    speechEvaluationConfig.context = context;
    speechEvaluationConfig.initialContent = richTextEditor.getSpeechEvaluationText() ?? "";

    modalId = richTextEditor.$modal.show(SpeechEvaluationModal, speechEvaluationConfig, {}, true);
  }

  function validationToCreate() {
    let isSpeechEvaluationInEditor = editor
      .getBody()
      .getElementsByClassName("sls-speech-evaluation");
    return isSpeechEvaluationInEditor.length === 0;
  }

  editor.ui.registry.addButton("speech_evaluation", {
    tooltip: i18n.t("tinymce_plugin_tooltip_icon_button_speech_evaluation"),
    icon: "speech-evaluation",
    onAction: function () {
      if (validationToCreate()) {
        openModal();
      } else {
        const props = {
          title: i18n.t("speech_evaluation_creation_validation_title"),
          content: i18n.t("speech_evaluation_creation_validation"),
          primaryBtnLabel: i18n.t("button_ok"),
          use_secondaryButton: false,
          actionPrimary: () => {},
        };
        Modal.$modal.showMessage("warning", props, true);
      }
    },
  });

  editor.on("click", (e) => {
    let target = e.target;
    if (target.closest("button.edit") && target.closest("button.edit").length !== 0) {
      openModal();
    }

    if (target.closest("button.trash") && target.closest("button.trash").length !== 0) {
      let targetNode = target.closest(".sls-speech-evaluation-wrapper");
      if (targetNode) editor.execCommand("removeSpeechEvaluation", targetNode);
    }

    // cater for cases when have undo action
    handlePluginRerender(target);
  });

  editor.addCommand("removeSpeechEvaluation", (targetNode) => {
    richTextUtils.removeNodeFromEditor(editor, targetNode);
  });
}
